/**
 * Mid-year board review builder — fires at the H1 close (Jun 30, M6 D30).
 *
 * Pure, side-effect-free. Returns a snapshot of H1 KPIs (months 1-6 of
 * the running year) shaped for `<MidYearBoardMeeting />`. The format
 * intentionally MIRRORS `buildAnnualReport` (same field names where
 * applicable) so the modal can reuse the same chart / card primitives.
 *
 * Returned shape:
 *   { year, months, totalRevenue, totalExpenses, netResult,
 *     treasuryStart, treasuryEnd, treasuryDelta,
 *     bestMonth, worstMonth,
 *     vehiclesTotal, driversTotal,
 *     reputation, notoriety, contractsCompleted,
 *     marginPct, runwayMonths,                    // H1-specific
 *     s1Highlights: { acquisitions, departures }, // H1-specific
 *     tenderStats: { won, lost, total, winRate, securedRevenue,
 *                    pending, bestClient, viaCounter } | null  // 02/06/2026
 *   }
 *
 * Returns `null` if the trigger conditions are not met (not on M6 D30, or
 * no months recorded for the running year yet).
 *
 * 31/05/2026 — Mid-year strategic board (informational only).
 * 02/06/2026 — Adds `tenderStats` covering the H1 AO window.
 */

import { buildH1TenderStats } from './tenderReportBuilder';

export function buildMidYearReview(state, finalTreasury, finalVehicles, driversNow) {
  if (!state) return null;
  // Only fire at the boundary day = end of month 6.
  // The caller passes the CURRENT state (just before the day flip), so
  // we expect month === 6 && day === 30. We also accept the post-flip
  // state (month === 7 && day === 1) for safety.
  const isM6Last = state.month === 6 && state.day >= 30;
  const isM7First = state.month === 7 && state.day === 1;
  if (!isM6Last && !isM7First) return null;
  if (!state.monthlyHistory || !Array.isArray(state.monthlyHistory)) return null;

  const runningYear = state.year || 1;
  const h1Months = state.monthlyHistory.filter(
    m => m.year === runningYear && m.month >= 1 && m.month <= 6,
  );
  if (h1Months.length === 0) return null;

  const totalRevenue = h1Months.reduce((s, m) => s + (m.totalRevenue || 0), 0);
  const totalExpenses = h1Months.reduce((s, m) => s + (m.totalExpenses || 0), 0);
  const netResult = totalRevenue - totalExpenses;
  const marginPct = totalRevenue > 0 ? Math.round((netResult / totalRevenue) * 100) : 0;

  // Best / worst month over H1.
  const bestMonth = h1Months.reduce(
    (a, b) => ((a.netResult ?? -Infinity) > (b.netResult ?? -Infinity) ? a : b),
  );
  const worstMonth = h1Months.reduce(
    (a, b) => ((a.netResult ?? Infinity) < (b.netResult ?? Infinity) ? a : b),
  );

  // Treasury delta over H1.
  const firstTreasury = h1Months[0]?.treasury ?? finalTreasury;
  const treasuryDelta = Math.round(finalTreasury) - Math.round(firstTreasury);

  // Runway projection (in months) at H1 close.
  const avgMonthlyCharges = totalExpenses / Math.max(1, h1Months.length);
  const runwayMonths = avgMonthlyCharges > 0
    ? Math.round((finalTreasury / avgMonthlyCharges) * 10) / 10
    : 999;

  // H1-specific highlights: fleet & driver moves over the semester.
  // Acquisitions: vehicles added with totalDays-relative metadata.
  const yearStartDay = (runningYear - 1) * 360;
  const h1EndDay = yearStartDay + 180;
  const acquisitions = (finalVehicles || []).filter(v => {
    const acquired = v.acquiredOnDay ?? v.acquiredAt ?? null;
    return typeof acquired === 'number' && acquired >= yearStartDay && acquired < h1EndDay;
  }).length;
  const departures = Object.values(state.firedDriversArchive || {})
    .filter(a => a && typeof a.firedAt === 'number' && a.firedAt >= yearStartDay && a.firedAt < h1EndDay)
    .length;

  return {
    year: runningYear,
    months: h1Months,
    totalRevenue: Math.round(totalRevenue),
    totalExpenses: Math.round(totalExpenses),
    netResult: Math.round(netResult),
    marginPct,
    treasuryStart: Math.round(firstTreasury),
    treasuryEnd: Math.round(finalTreasury),
    treasuryDelta,
    bestMonth: { month: bestMonth.month, netResult: bestMonth.netResult },
    worstMonth: { month: worstMonth.month, netResult: worstMonth.netResult },
    vehiclesTotal: (finalVehicles || []).length,
    driversTotal: (driversNow || []).length,
    reputation: Math.round(state.reputation || 0),
    notoriety: Math.round(state.notoriety || 0),
    contractsCompleted: h1Months.reduce((s, m) => s + (m.contractsCompleted || 0), 0),
    runwayMonths,
    s1Highlights: { acquisitions, departures },
    // 02/06/2026 — Tender / AO recap for the H1 window. Null when no
    // AO activity AND no pending applications (panel hides gracefully).
    tenderStats: buildH1TenderStats(state, runningYear),
  };
}
